import React from "react";
import { Card, Computed } from "./shared";
import * as m from "../lib/model";

const R_OF = (M) => m.rollupCfgFor(M);

// Typed entry rather than sliders — leverage terms are negotiated numbers, not something you drag.
const Row = ({ label, value, onChange, min, max, step, unit = "%", testid, sub }) => (
  <div className="lev-row">
    <span className="lev-label">{label}{sub && <em className="lev-sub">{sub}</em>}</span>
    <span className="lev-input">
      <input className="inp num" type="number" min={min} max={max} step={step} value={value === 0 ? 0 : value ?? ""}
        onFocus={(e) => e.target.select()}
        onClick={(e) => e.target.select()}
        onChange={(e) => {
          const raw = String(e.target.value).replace(/^0+(?=\d)/, "");
          const n = raw === "" ? 0 : Number(raw);
          onChange(Math.min(max == null ? n : max, Math.max(min == null ? n : min, n)));
        }} data-testid={testid} />
      <i>{unit}</i>
    </span>
  </div>
);

/** Add-on financing: what each bolt-on is funded with, on what terms, over what length. */
export function LeveragePanel({ M, setField }) {
  const R = R_OF(M);
  const setR = (p) => setField("rollup", { ...R, ...p });
  const out = m.rollupModelFor(M);
  const addons = out.rows.filter((r) => r.year > 0);
  const eqPct = Math.max(0, 100 - (R.debtPct || 0) - (R.sellerNotePct || 0) - (R.rolloverPct || 0));
  const snRate = m.addonSnRate(M);
  const snYrs = m.addonSnYrs(M);
  const debtYrs = m.addonDebtYrs(M);
  const rate = Number(R.debtRate) || M.rate || 0;
  const dsOf = (r) => m.annuity(r.debt || 0, rate, debtYrs) + m.annuity(r.sellerNote || 0, snRate, snYrs);
  const totDs = addons.reduce((a, r) => a + dsOf(r), 0);
  const totEbitda = addons.reduce((a, r) => a + (r.ebitda || 0), 0);
  const dscr = totDs > 0 ? totEbitda / totDs : 0;

  return (
    <div data-testid="leverage-panel">
      <Card title="Add-On Financing — Capital Structure" testid="lev-structure">
        <div className="bullet">How every add-on is funded as a share of its purchase price. Sponsor equity is the plug, so these three shares plus equity always make 100%.</div>
        <Row label="Senior debt" sub="bank / SBA facility" value={R.debtPct || 0} onChange={(v) => setR({ debtPct: v })} min={0} max={100} step={0.5} testid="lev-debtPct" />
        <Row label="Seller note" sub="subordinated, seller-financed" value={R.sellerNotePct || 0} onChange={(v) => setR({ sellerNotePct: v })} min={0} max={100} step={0.5} testid="lev-snPct" />
        <Row label="Seller rollover" sub="equity the seller keeps" value={R.rolloverPct || 0} onChange={(v) => setR({ rolloverPct: v })} min={0} max={100} step={0.5} testid="lev-roPct" />
        <Computed label="Sponsor equity % of price (plug)" value={`${eqPct}%`} tone={eqPct <= 0 ? "pass" : "go"} testid="lev-eqPct" />
        {eqPct <= 0 && <div className="banner warn" data-testid="lev-overfunded"><span>⚠</span><span>Debt, seller note and rollover already cover the whole price — no equity is left to invest, so returns will read as infinite. Pull one of them back.</span></div>}
      </Card>

      <Card title="Senior Debt Terms" testid="lev-senior" style={{ marginTop: 12 }}>
        <Row label="Interest rate" value={rate} onChange={(v) => setR({ debtRate: v })} min={0} max={30} step={0.125} testid="lev-debtRate" />
        <Row label="Amortisation term" sub="years to fully repay" value={debtYrs} onChange={(v) => setR({ debtYrs: v })} min={1} max={30} step={1} unit="years" testid="lev-debtYrs" />
        <Row label="Paydown by exit" sub="share of add-on debt retired" value={R.debtPaydownPct || 0} onChange={(v) => setR({ debtPaydownPct: v })} min={0} max={100} step={1} testid="lev-paydown" />
        <Computed label="Platform facility (for reference)" value={`${m.fmt$(m.loanAmt(M))} at ${M.rate}% over ${M.loanYrs} yr`} testid="lev-platform-ref" />
      </Card>

      <Card title="Seller Note Terms" testid="lev-sellernote" style={{ marginTop: 12 }}>
        <div className="bullet">Leave these at the platform's terms unless add-on sellers are financing on different paper.</div>
        <Row label="Interest rate" value={snRate} onChange={(v) => setR({ snRate: v })} min={0} max={30} step={0.125} testid="lev-snRate" />
        <Row label="Term" sub="years to fully repay" value={snYrs} onChange={(v) => setR({ snYrs: v })} min={1} max={30} step={1} unit="years" testid="lev-snYrs" />
        {(R.snRate != null && R.snRate !== "") || (R.snYrs != null && R.snYrs !== "") ? (
          <button className="btn btn-sm" onClick={() => setR({ snRate: null, snYrs: null })} data-testid="lev-sn-reset">Match the platform ({M.snRate}% over {M.snYrs} yr)</button>
        ) : <Computed label="Source" value={`Inherited from the platform — ${M.snRate}% over ${M.snYrs} yr`} testid="lev-sn-source" />}
      </Card>

      <Card title="Financing by Add-On" testid="lev-table" className="table-scroll" style={{ marginTop: 12 }}>
        <table className="tbl">
          <thead><tr>
            <th>Add-on</th><th className="num">Year</th><th className="num">Price</th><th className="num">Senior debt</th>
            <th className="num">Seller note</th><th className="num">Rollover</th><th className="num">Equity</th>
            <th className="num">Annual debt service</th><th className="num">EBITDA</th><th className="num">DSCR</th>
          </tr></thead>
          <tbody>
            {addons.map((r, i) => {
              const ds = dsOf(r);
              const cov = ds > 0 ? (r.ebitda || 0) / ds : 0;
              return (
                <tr key={i} data-testid={`lev-row-${i}`}>
                  <td>{r.name}</td><td className="num">Yr {r.year}</td><td className="num">{m.fmt$(r.price)}</td>
                  <td className="num">{r.debt ? m.fmt$(r.debt) : "—"}</td>
                  <td className="num">{r.sellerNote ? m.fmt$(r.sellerNote) : "—"}</td>
                  <td className="num">{r.rollover ? m.fmt$(r.rollover) : "—"}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{m.fmt$(r.equity)}</td>
                  <td className="num exp">{ds ? m.fmt$(-ds) : "—"}</td>
                  <td className="num">{m.fmt$(r.ebitda)}</td>
                  <td className={`num ${cov >= 1.5 ? "pos" : cov > 0 ? "neg" : ""}`}>{ds ? m.fmtX(cov) : "n/a"}</td>
                </tr>
              );
            })}
            {!addons.length && <tr><td colSpan={10} style={{ color: "var(--text3)" }}>No add-ons scheduled — add them on Assumptions → Roll-Up.</td></tr>}
            <tr className="total" data-testid="lev-total">
              <td>All add-ons</td><td className="num">—</td><td className="num">{m.fmt$(addons.reduce((a, r) => a + r.price, 0))}</td>
              <td className="num">{m.fmt$(addons.reduce((a, r) => a + (r.debt || 0), 0))}</td>
              <td className="num">{m.fmt$(addons.reduce((a, r) => a + (r.sellerNote || 0), 0))}</td>
              <td className="num">{m.fmt$(addons.reduce((a, r) => a + (r.rollover || 0), 0))}</td>
              <td className="num">{m.fmt$(addons.reduce((a, r) => a + (r.equity || 0), 0))}</td>
              <td className="num exp">{totDs ? m.fmt$(-totDs) : "—"}</td>
              <td className="num">{m.fmt$(totEbitda)}</td>
              <td className={`num ${dscr >= 1.5 ? "pos" : dscr > 0 ? "neg" : ""}`}>{totDs ? m.fmtX(dscr) : "n/a"}</td>
            </tr>
          </tbody>
        </table>
        <div className="ra-sub" style={{ marginTop: 8 }}>
          Senior debt amortises over {debtYrs} years at {rate}%; seller notes over {snYrs} years at {snRate}%. Debt service starts the year after each close and flows into the consolidated cash flow, the DSCR on Returns and the investor deal room.
        </div>
      </Card>
    </div>
  );
}
